import classNames from "classnames";

interface PanelTitleProps {
  /** The label shown before the count (e.g. "Steps", "Annotations"). */
  label: string;
  /**
   * The count to display — the filtered count when filters are active,
   * otherwise the total. Pass `undefined` to render the label on its own.
   */
  count?: number;
  /**
   * The total count, shown as "of {totalCount}" when defined. Pass `undefined`
   * (the default) when no filter is active to render just the count.
   */
  totalCount?: number;
  /** Optional `data-testid` for tests. */
  testId?: string;
  /** Optional extra class names appended to the root element. */
  className?: string;
}

/**
 * Compact panel title used in toolbars to show "{label} {count}" with an
 * optional dimmed "of {totalCount}" suffix when a filter is active.
 *
 * The label is rendered as a bold, dark heading; the count is rendered as
 * smaller, lighter metadata. Used by `StepsCount` and `AnnotationsCount` to
 * keep the styling consistent.
 */
export default function PanelTitle({
  label,
  count,
  totalCount,
  testId,
  className,
}: PanelTitleProps) {
  const hasCount = count !== undefined;
  const isFiltered = totalCount !== undefined;

  return (
    <div
      data-testid={testId}
      className={classNames("flex items-baseline gap-1.5 leading-4", className)}
    >
      <span className="text-charcoal-700 text-sm font-medium">{label}</span>
      {hasCount && (
        <span className="text-charcoal-300 text-xs/5">
          {count}
          {isFiltered && ` of ${totalCount}`}
        </span>
      )}
    </div>
  );
}
